import { useEffect } from "react";
import QRCode from "react-qr-code";

import { AnalyticsHelper } from "@core/Analytics/AnalyticsHelper";

import Map from "@components/Mapa/Map";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { usePopup } from "@contexts/popupContext";
import { HollowedButton } from "@shared/designSystem/Button";
import { ColumnsContainer, HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import DetailField from "@shared/designSystem/DetailField";
import DetailSection from "@shared/designSystem/DetailSection";
import { Modal } from "@shared/designSystem/messages/Modal";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";

import { QRCard } from "./QRCard";
import {
    QR_SHEET_FOREGROUND_COLOR,
    QRMode,
    QontactPointDetailData,
    qrExportSizeInPixels,
    useQontactPointDetailModalViewModel,
} from "./QontactPointDetailModalViewModel";

// Visually hidden but still present in the DOM (so its <svg> can be read for
// export) — not display:none, since layout, not paint, is what we're hiding.
const hiddenExportContainerStyle: React.CSSProperties = {
    position: "absolute",
    width: 0,
    height: 0,
    overflow: "hidden",
    pointerEvents: "none",
};

interface QontactPointDetailModalScreenProps {
    qontactPoint: QontactPointDetailData;
    qrMode: QRMode;
}

const QontactPointDetailModalScreen: React.FC<QontactPointDetailModalScreenProps> = ({
    qontactPoint,
    qrMode,
}) => {
    const { hidePopup } = usePopup();
    const {
        whatsAppQrRef,
        appQrRef,
        whatsAppExportQrRef,
        appExportQrRef,
        whatsAppQRValue,
        appQRValue,
        showWhatsAppQR,
        showAppQR,
        qrState,
        retryQR,
        googleMapsURL,
        mapMarkers,
        mapCircles,
        triggerDistanceInMeters,
        downloadWhatsAppQR,
        downloadAppQR,
    } = useQontactPointDetailModalViewModel(qontactPoint, qrMode);

    useEffect(() => {
        AnalyticsHelper.trackQontactPointDetailShown(
            qontactPoint.id,
            qontactPoint.buildingID,
            qontactPoint.functionalUnitID
        );
    }, [qontactPoint.buildingID, qontactPoint.functionalUnitID, qontactPoint.id]);

    const handleDownloadWhatsAppQR = async () => {
        AnalyticsHelper.trackQontactPointQRDownloaded(
            qontactPoint.id,
            qontactPoint.buildingID,
            qontactPoint.functionalUnitID,
            "whatsapp"
        );
        await downloadWhatsAppQR();
    };

    const handleDownloadAppQR = async () => {
        AnalyticsHelper.trackQontactPointQRDownloaded(
            qontactPoint.id,
            qontactPoint.buildingID,
            qontactPoint.functionalUnitID,
            "app"
        );
        await downloadAppQR();
    };

    return (
        <Modal
            isOpen={true}
            onClose={hidePopup}
            title={"Detalle del Qontact Point".localized()}
            width="880px"
            height="85vh"
            maxWidth="94vw"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
                <HorizontalContainer width="100%" gap={designSystemStyles.gapSm} alignment="center">
                    <HorizontalContainer
                        width="44px"
                        height="44px"
                        minWidth="44px"
                        alignment="center"
                        justifyContent="center"
                        backgroundColor={designSystemStyles.colorBackgroundGray2}
                        borderRadius={designSystemStyles.cornerRadiusComponent}
                    >
                        <SystemIcon type="building" color={designSystemStyles.colorText} />
                    </HorizontalContainer>
                    <VerticalContainer width="auto" margin="0" gap={designSystemStyles.gapXxxSm}>
                        <Text text={`**${(qontactPoint.name ?? '').trim()}**`} markdown={true} />
                        <SmallText text={"Qontact Point".localized()} color={designSystemStyles.colorTextSecondary} />
                    </VerticalContainer>
                </HorizontalContainer>

                <VerticalContainer width="100%" height="100%" scrollable={true} gap={designSystemStyles.gapLg}>
                    <VerticalContainer width="100%" gap={designSystemStyles.gapLg} paddingBottom={designSystemStyles.paddingSm}>
                        <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
                            <Text text={`**${"Códigos QR".localized()}**`} markdown={true} color={designSystemStyles.colorText} />
                            <ColumnsContainer minColumnWidth="320px" gap={designSystemStyles.gapSm}>
                                {showWhatsAppQR && (
                                    <QRCard
                                        iconType="send"
                                        title={"QR de WhatsApp".localized()}
                                        subtitle={"Check-in escaneando desde WhatsApp".localized()}
                                        downloadTitle={"Descargar QR de WhatsApp".localized()}
                                        onDownload={() => void handleDownloadWhatsAppQR()}
                                        value={whatsAppQRValue}
                                        state={qrState}
                                        onRetry={retryQR}
                                        qrRef={whatsAppQrRef}
                                    />
                                )}
                                {showAppQR && (
                                    <QRCard
                                        iconType="qr"
                                        title={"QR de la app".localized()}
                                        subtitle={"Check-in desde la app de Qontact".localized()}
                                        downloadTitle={"Descargar QR de la app".localized()}
                                        onDownload={() => void handleDownloadAppQR()}
                                        value={appQRValue}
                                        state={qrState}
                                        onRetry={retryQR}
                                        qrRef={appQrRef}
                                    />
                                )}
                            </ColumnsContainer>
                            {showWhatsAppQR && (
                                <div ref={whatsAppExportQrRef} style={hiddenExportContainerStyle} aria-hidden="true">
                                    <QRCode
                                        value={whatsAppQRValue}
                                        size={qrExportSizeInPixels}
                                        level="H"
                                        fgColor={QR_SHEET_FOREGROUND_COLOR}
                                    />
                                </div>
                            )}
                            {showAppQR && (
                                <div ref={appExportQrRef} style={hiddenExportContainerStyle} aria-hidden="true">
                                    <QRCode
                                        value={appQRValue}
                                        size={qrExportSizeInPixels}
                                        level="H"
                                        fgColor={QR_SHEET_FOREGROUND_COLOR}
                                    />
                                </div>
                            )}
                        </VerticalContainer>

                        <DetailSection title={"Coordenadas".localized()}>
                            <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                                    <SmallText text={"Ubicación".localized()} color={designSystemStyles.colorText} />
                                    <a
                                        href={googleMapsURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: "none" }}
                                    >
                                        <Text
                                            text={`${qontactPoint.latitude}, ${qontactPoint.longitude}`}
                                            color={designSystemStyles.colorMainAction}
                                        />
                                    </a>
                                </VerticalContainer>
                            </ColumnsContainer>
                        </DetailSection>

                        <DetailSection title={"Mapa".localized()}>
                            <VerticalContainer width="100%" height="320px">
                                {mapMarkers.length > 0 ? (
                                    <Map
                                        markers={mapMarkers}
                                        circles={mapCircles}
                                        radius={designSystemStyles.cornerRadiusComponent}
                                        setMapRefParent={() => {}}
                                    />
                                ) : (
                                    <QontyEmptyState
                                        text={"No hay coordenadas suficientes para mostrar el mapa.".localized()}
                                        fullSize={false}
                                    />
                                )}
                            </VerticalContainer>
                        </DetailSection>

                        <DetailSection title={"Resumen".localized()}>
                            <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                <DetailField label={"ID".localized()} value={qontactPoint.id} />
                                <DetailField label={"Nombre".localized()} value={qontactPoint.name} />
                                <DetailField label={"Building ID".localized()} value={qontactPoint.buildingID} />
                                <DetailField label={"Functional Unit ID".localized()} value={qontactPoint.functionalUnitID} />
                                <DetailField label={"Turnos totales".localized()} value={qontactPoint.totalShifts} />
                                <DetailField
                                    label={"Radio permitido para check-ins".localized()}
                                    value={`${triggerDistanceInMeters} m`}
                                />
                            </ColumnsContainer>
                        </DetailSection>

                    </VerticalContainer>
                </VerticalContainer>

                <HorizontalContainer justifyContent="flex-end" width="100%">
                    <HollowedButton text={"Cerrar".localized()} onClick={hidePopup} width="120px" />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

export default QontactPointDetailModalScreen;
